import { apiListaEspera } from '@modules/professional-waitlist/api/apiListaEspera'

const STATUS_OK = 200
const STATUS_CONFLITO = 409 // AC-2.4 — revisão desatualizada, outra aba gravou primeiro

const MSG_ERRO_CARREGAR = 'Não foi possível carregar o questionário. Tente novamente mais tarde.'
const MSG_ERRO_RESPONDER = 'Não foi possível salvar sua resposta. Tente novamente.'
const MSG_ERRO_REFAZER = 'Não foi possível reabrir o questionário. Tente novamente mais tarde.'
const MSG_CONFLITO =
  'Suas respostas foram atualizadas em outra aba. Confira os dados acima e envie de novo.'

// GET /questionario — chamado no boot da tela e sempre que o estado do servidor pode
// ter mudado por fora do que o cliente sabe (409 de outra aba). Nunca mexe em
// `rascunho`: é dado da TELA, não do servidor (ver `mutations.js::SET_ESTADO`).
export async function carregar({ commit }) {
  commit('SET_LOADING', true)

  const r = await apiListaEspera.carregar()

  if (r && r.status === STATUS_OK) {
    commit('SET_ESTADO', r.value)
    commit('SET_ERRO', null)
  } else {
    commit('SET_ERRO', (r && r.message) || MSG_ERRO_CARREGAR)
  }

  commit('SET_LOADING', false)
  return r
}

// PUT /questionario/resposta — grava a resposta de UM campo (AC-2.1). Continua sendo
// uma chamada por campo mesmo depois da v2 em etapas: é ela que faz "não perder dados"
// ser verdade (contracts/etapas-do-questionario.md, §"Avanço por ETAPA, não por campo").
// - 200: aplica o estado novo devolvido pelo servidor e descarta o rascunho DESSE campo.
// - 409 (AC-2.4): o corpo do 409 NÃO traz `value` novo (`questionario-resposta.json`) —
//   sobrescrever com ele apagaria o estado. Em vez disso, relê pelo GET (`carregar`),
//   que também não toca `rascunho` — é assim que a digitação corrente sobrevive
//   (T013: test_409_relê_sem_perder_digitacao).
// - qualquer outro status (422/500/403 — AC-2.3): mantém a pergunta corrente, só
//   expõe o erro; NUNCA avança em silêncio.
export async function responder(
  { commit, dispatch, state },
  { campo, valor, valorComplementar, campoComplementar }
) {
  commit('SET_ENVIANDO', true)
  commit('SET_ERRO', null)

  const r = await apiListaEspera.responder({
    campo: campo,
    valor: valor,
    valorComplementar: valorComplementar,
    revisaoEsperada: state.revisao,
  })

  if (r && r.status === STATUS_OK) {
    commit('SET_ESTADO', r.value)
    // O rascunho do campo complementar (`cargo_outro`) é uma chave PRÓPRIA no
    // mapa — sem limpá-la junto, o texto digitado continuaria vencendo a
    // resposta confirmada pelo servidor na precedência da tela.
    commit('LIMPAR_RASCUNHO', campoComplementar ? [campo, campoComplementar] : [campo])
  } else if (r && r.status === STATUS_CONFLITO) {
    await dispatch('carregar')
  } else {
    commit('SET_ERRO', (r && r.message) || MSG_ERRO_RESPONDER)
  }

  commit('SET_ENVIANDO', false)
  return r
}

/**
 * Grava a ETAPA inteira — um PUT por campo, EM SEQUÊNCIA.
 *
 * ★ A rota não mudou (contracts/etapas-do-questionario.md): o que mudou foi a TELA,
 * que junta as respostas da etapa e as envia ao clicar em "Enviar respostas". Um PUT
 * por campo é o que sustenta a promessa de não perder dado — quem fechar o navegador
 * no meio da sequência mantém o que já foi gravado.
 *
 * ⚠️ SEQUENCIAL, nunca em paralelo: cada 200 devolve uma `revisao` nova, e
 * `responder` lê `state.revisao` na hora da chamada. Disparadas juntas, da segunda em
 * diante todas mandariam a revisão velha e levariam 409.
 *
 * ⚠️ Para na PRIMEIRA falha e devolve a resposta ofensora. As anteriores JÁ FORAM
 * gravadas e continuam gravadas; a etapa se mantém na tela com o erro, sem avançar
 * com buraco.
 */
export async function responderEtapa({ commit, dispatch }, respostas) {
  const lista = Array.isArray(respostas) ? respostas : []

  for (let i = 0; i < lista.length; i += 1) {
    const r = await dispatch('responder', lista[i])

    if (!r || r.status !== STATUS_OK) {
      // O 409 é o único status que `responder` trata sem escrever mensagem nenhuma
      // (ele relê o servidor em silêncio). Sem isto, a etapa pararia de avançar e a
      // tela não diria por quê — falha silenciosa é bug duplo.
      if (r && r.status === STATUS_CONFLITO) commit('SET_ERRO', MSG_CONFLITO)
      return r || { status: 500, message: MSG_ERRO_RESPONDER, value: null }
    }
  }

  return { status: STATUS_OK, message: '', value: null }
}

// POST /questionario/refazer — não-destrutivo por construção (AC-4.1/4.2/4.4): o
// backend preserva as 7 respostas e a posição já atribuída; aqui só refletimos o
// estado novo (campo_atual volta a 'nome').
export async function refazer({ commit }) {
  commit('SET_ENVIANDO', true)
  commit('SET_ERRO', null)

  const r = await apiListaEspera.refazer()

  if (r && r.status === STATUS_OK) {
    commit('SET_ESTADO', r.value)
    commit('LIMPAR_RASCUNHO')
  } else {
    commit('SET_ERRO', (r && r.message) || MSG_ERRO_REFAZER)
  }

  commit('SET_ENVIANDO', false)
  return r
}

// O componente chama isto a cada tecla — puro commit local, sem rede (rascunho é
// estado da TELA). Payload `{ campo, valor }`: a etapa tem vários campos abertos ao
// mesmo tempo, então a digitação é endereçada por campo.
export function setRascunho({ commit }, payload) {
  commit('SET_RASCUNHO', payload)
}
